import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserPlus, Trash2, ShieldCheck, Loader2, Plug, CheckCircle2, XCircle, MessageSquare, Copy, Link2 } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const [data, setData] = useState({ owner: "", staff: [] });
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/staff"); setData(r.data); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try { await api.post("/staff", { email: email.trim() }); setEmail(""); toast.success("Staff authorized"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const remove = async (em) => {
    try { await api.delete(`/staff/${encodeURIComponent(em)}`); load(); toast.success("Access removed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Layout>
      <div className="mb-6">
        <p className="mono text-xs uppercase tracking-[0.2em] text-brand mb-2">Access & Integrations</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Settings</h1>
      </div>

      <div className="max-w-2xl space-y-6">
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="font-heading text-lg font-semibold mb-1">Authorized lab employees</h3>
          <p className="text-sm text-muted-foreground mb-5">Only these Google accounts can sign in to the console.</p>
          <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary mb-3">
            <ShieldCheck className="w-5 h-5 text-brand" />
            <div className="flex-1"><div className="text-sm font-medium">{data.owner}</div>
              <div className="mono text-[10px] uppercase tracking-wider text-muted-foreground">Owner · full access</div></div>
          </div>
          <div className="space-y-2 mb-5" data-testid="staff-list">
            {data.staff.map((s) => (
              <div key={s} className="flex items-center gap-3 p-3 rounded-lg border border-border">
                <div className="flex-1 text-sm">{s}</div>
                {user?.is_owner && <button onClick={() => remove(s)} data-testid={`remove-staff-${s}`} className="text-ruby hover:opacity-70"><Trash2 className="w-4 h-4" /></button>}
              </div>
            ))}
            {data.staff.length === 0 && <p className="text-sm text-muted-foreground">No additional staff yet.</p>}
          </div>
          {user?.is_owner ? (
            <div className="flex gap-2">
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="employee@email.com" data-testid="staff-email-input" />
              <Button onClick={add} disabled={busy} className="bg-brand hover:bg-brand-hover text-primary-foreground" data-testid="add-staff-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1.5" />} Add
              </Button>
            </div>
          ) : <p className="mono text-[11px] text-muted-foreground">Only the lab owner can add or remove staff.</p>}
        </div>

        <IntegrationsCard isOwner={user?.is_owner} />
      </div>
    </Layout>
  );
}

function IntegrationsCard({ isOwner }) {
  const [st, setSt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [env, setEnv] = useState("production");
  const [token, setToken] = useState("");
  const [whKey, setWhKey] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/integrations/status"); setSt(r.data); if (r.data.square?.env) setEnv(r.data.square.env); } catch {}
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    if (!token.trim()) { toast.error("Paste your Square access token"); return; }
    setConnecting(true);
    try {
      const r = await api.post("/square/connect", { access_token: token.trim(), env, webhook_signature_key: whKey.trim() || undefined });
      if (r.data.ok) { toast.success("Square connected — live sync active!"); setToken(""); }
      else toast.error("Could not connect: " + (r.data.error || "check token & environment"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setConnecting(false); }
  };

  const copy = async (t) => {
    try {
      await navigator.clipboard.writeText(t);
      toast.success("Copied");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = t; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); toast.success("Copied"); }
      catch { toast.message("Copy this URL", { description: t }); }
      document.body.removeChild(ta);
    }
  };

  const Row = ({ ok, children }) => (
    <div className="flex items-start gap-2 text-sm">
      {ok ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#10B981" }} /> : <XCircle className="w-4 h-4 text-ruby shrink-0 mt-0.5" />}
      <span className={ok ? "" : "text-muted-foreground"}>{children}</span>
    </div>
  );

  const sq = st?.square || {};
  return (
    <div className="bg-card border border-border rounded-xl p-6" data-testid="integrations-card">
      <h3 className="font-heading text-lg font-semibold flex items-center gap-2 mb-1"><Plug className="w-5 h-5 text-brand" /> Integrations</h3>
      <p className="text-sm text-muted-foreground mb-5">Live connections for order intake and notifications.</p>

      {loading ? <div className="py-6 text-center text-muted-foreground mono text-sm animate-pulse">Checking…</div> : st && (
        <div className="space-y-5">
          <div className="rounded-lg border border-border p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="font-medium">Square</span>
              <span className="mono text-xs text-muted-foreground">{sq.env}{sq.location_name ? ` · ${sq.location_name}` : ""}</span>
            </div>
            <div className="space-y-2 mb-2">
              <Row ok={sq.connected}>{sq.connected ? "Connected — live sync active" : "Not connected"}</Row>
              <Row ok={sq.token_set}>{sq.token_set ? "Access token saved" : "Access token not set"}</Row>
              <Row ok={sq.webhook_key_set}>{sq.webhook_key_set ? "Webhook signature key saved" : "Webhook key not set (needed for auto-intake)"}</Row>
            </div>
            {sq.error && !sq.connected && <p className="mono text-[11px] text-ruby/80 break-words">{sq.error}</p>}

            {isOwner ? (
              <div className="mt-3 space-y-4">
                <div className="rounded-md bg-secondary p-3 text-xs text-muted-foreground leading-relaxed">
                  In the <b>Square Developer Console</b> → your app → <b>Credentials</b>, copy your <b>Access Token</b>. For auto-intake, add a <b>Webhook subscription</b> for <span className="mono">order.created, order.updated</span> pointing at the URL below, then paste its <b>Signature Key</b> here.
                  <div className="flex items-center gap-2 mt-2">
                    <code className="flex-1 bg-background rounded px-2 py-1.5 text-[11px] break-all">{sq.webhook_url}</code>
                    <Button variant="outline" size="sm" onClick={() => copy(sq.webhook_url)} data-testid="copy-webhook-url-btn"><Copy className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEnv("production")} className={`px-3 py-1.5 rounded-md text-xs font-medium ${env === "production" ? "bg-brand text-primary-foreground" : "bg-secondary text-muted-foreground"}`} data-testid="env-production">Production</button>
                  <button onClick={() => setEnv("sandbox")} className={`px-3 py-1.5 rounded-md text-xs font-medium ${env === "sandbox" ? "bg-brand text-primary-foreground" : "bg-secondary text-muted-foreground"}`} data-testid="env-sandbox">Sandbox</button>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <div><Label className="text-xs">Access Token</Label>
                    <Input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Square access token" className="mt-1.5" data-testid="square-access-token" /></div>
                  <div><Label className="text-xs">Webhook Signature Key (optional)</Label>
                    <Input type="password" value={whKey} onChange={(e) => setWhKey(e.target.value)} placeholder="From your webhook subscription" className="mt-1.5" data-testid="square-webhook-key" /></div>
                </div>
                <Button onClick={connect} disabled={connecting || !token.trim()} className="bg-brand hover:bg-brand-hover text-primary-foreground" data-testid="connect-square-btn">
                  {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4 mr-1.5" />} {sq.connected ? "Update / reconnect" : "Connect Square"}
                </Button>
              </div>
            ) : <p className="mono text-[11px] text-muted-foreground mt-2">Only the lab owner can connect Square.</p>}
          </div>

          <div className="rounded-lg border border-border p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="font-medium flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Pickup SMS (Twilio)</span>
              {st.sms.from && <span className="mono text-xs text-muted-foreground">{st.sms.from}</span>}
            </div>
            <div className="space-y-2">
              <Row ok={st.sms.configured}>{st.sms.configured ? "Twilio configured — texts send on pickup-ready" : "Twilio not configured — SMS is skipped"}</Row>
              <div className="text-xs text-muted-foreground">Texts fire on: <span className="mono">{(st.sms.steps || []).join(", ")}</span></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
